// Drives the hero slider with Splide (true infinite looping - type: 'loop' -
// and a centered, peeking "active slide in the middle" layout on desktop),
// and syncs the tab bar, .collection-tab__inner's color scheme, description
// panels and product grids to whichever slide is active. Clicking a tab
// jumps Splide to the matching slide; Splide's own "moved" event is the
// single source of truth for which collection is active, so the two can
// never fall out of sync with each other.
class CollectionTabsSlider extends HTMLElement {
  connectedCallback() {
    this.sliderEl = this.querySelector('.splide');
    this.tabs = Array.from(this.querySelectorAll('[data-collection-tabs-tab]'));
    this.tabBar = this.querySelector('[data-collection-tabs-bar]');
    this.inner = this.querySelector('[data-collection-tabs-inner]');
    this.panels = Array.from(this.querySelectorAll('[data-collection-tabs-panel]'));
    this.grids = Array.from(this.querySelectorAll('[data-collection-tabs-grid]'));

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        this.splide ? this.splide.go(Number(tab.dataset.index)) : this.setActive(Number(tab.dataset.index));
      });
    });

    this.querySelectorAll('[data-collection-tabs-scroll]').forEach((button) => {
      button.addEventListener('click', () => {
        this.tabBar?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    if (this.sliderEl && window.Splide) {
      this.initSplide();
    } else if (this.sliderEl) {
      // Splide loads with `defer` from a CDN; if this somehow runs first,
      // wait for it rather than silently rendering an unstyled slide list.
      window.addEventListener('load', () => this.initSplide(), { once: true });
    }

    this.setActive(0);
  }

  initSplide() {
    if (!window.Splide || this.splide) return;

    this.splide = new window.Splide(this.sliderEl, {
      type: 'loop',
      perPage: 1,
      focus: 'center',
      gap: 0,
      padding: { left: '25%', right: '25%' },
      arrows: true,
      pagination: false,
      drag: true,
      breakpoints: {
        749: {
          padding: 0,
        },
      },
    });

    this.splide.on('moved', (newIndex) => this.setActive(newIndex));
    this.splide.mount();
  }

  setActive(index) {
    if (this.activeIndex === index) return;
    this.activeIndex = index;

    let activeScheme = null;

    this.tabs.forEach((tab) => {
      const isActive = Number(tab.dataset.index) === index;
      tab.classList.toggle('collection-tabs__tab--active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      if (isActive) {
        tab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        activeScheme = tab.dataset.scheme;
      }
    });

    // Swap both the tab bar and the description/products wrapper onto the
    // active collection's own color scheme class (the same "toggle the
    // scheme class" technique used for the transparent header), rather than
    // computing a color - the scheme's own --color-background/
    // --color-foreground stay guaranteed legible together.
    [this.tabBar, this.inner].forEach((el) => {
      if (!el || !activeScheme) return;
      if (el.dataset.activeScheme) el.classList.remove(el.dataset.activeScheme);
      el.classList.add(activeScheme);
      el.dataset.activeScheme = activeScheme;
    });

    this.panels.forEach((panel) => {
      panel.hidden = Number(panel.dataset.index) !== index;
    });

    this.grids.forEach((grid) => {
      grid.hidden = Number(grid.dataset.index) !== index;
    });
  }
}

customElements.define('collection-tabs-slider', CollectionTabsSlider);
